// @flow
import type { AbstractComponent } from 'react';
import styled from '@emotion/styled';
import Icon from '@tablekit/icon';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons/faArrowRight';
import { SPACING, typography } from '@tablekit/theme';
import type { MediaQueryPropType } from '@tablekit/utils/lib/mediaQuery';
import type {
  BlokComponentPropsType,
  TestimonialBlockType
} from 'components/types';
import { EDITOR_VALUES, handleEditorValues } from 'components/PageContext';
import PersonImage, { SIZES } from 'components/PersonImage';
import { CardHoverLink, Card as BaseCard } from '../styled';
import type { CardPropsType } from '../types';
import { isValidLink } from '../../../Link';

export const SMALL_SCREEN_HEIGHT = 346;
export const LARGE_SCREEN_HEIGHT = 305;
export const LARGE_SCREEN_WIDTH = 1024;

export const TestimonialGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;

  @media (min-width: 1024px) {
    margin: 0 -${SPACING.L3};
    flex-direction: row;
  }
`;

export const CoreTestimonialCard = styled(BaseCard)`
  padding: ${SPACING.L6};
  text-align: left;
  height: ${SMALL_SCREEN_HEIGHT};

  @media (min-width: ${LARGE_SCREEN_WIDTH}px) {
    height: ${LARGE_SCREEN_HEIGHT}px;
  }
`;

const StaticTestimonialCard = styled(CoreTestimonialCard)`
  margin: 0 0 ${SPACING.L6};
  width: 100%;

  @media (min-width: 1024px) {
    margin: 0 ${SPACING.L3} ${SPACING.L3};
    width: calc(50% - ${SPACING.L3} - ${SPACING.L3});
  }
`;

const ContentText = styled.div`
  ${typography('L3')};
  @media (min-width: ${LARGE_SCREEN_WIDTH}px) {
    ${typography('L4')};
  }
`;

const QuoteText = styled.div`
  ${typography('L3')};
  @media (min-width: ${LARGE_SCREEN_WIDTH}px) {
    ${typography('L5')};
  }
`;

const QuoteAuthor = styled.div`
  display: flex;
  ${typography('L2')};
  flex-direction: row;
  margin-top: auto;
  justify-content: flex-start;
  align-items: flex-end;

  & > span {
    padding-right: ${SPACING.L2};
  }
`;

const QuoteAuthorText = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 100%;
  margin: 0 ${SPACING.L4};
`;

const ClientSubtext = styled.div`
  ${typography('L1')};
  @media (min-width: ${LARGE_SCREEN_WIDTH}px) {
    ${typography('L2')};
  }
`;

export function TestimonialCard({
  blok: { content: blockContent },
  component: Component = StaticTestimonialCard,
  imageSize = {
    default: SIZES.DEFAULT,
    'min-width: 576px': SIZES.QUOTE,
    'min-width: 1024px': SIZES.DEFAULT,
    'min-width: 1280px': SIZES.QUOTE
  },
  ...others
}: BlokComponentPropsType<TestimonialBlockType> & {
  component: AbstractComponent<*, *>,
  imageSize: MediaQueryPropType<string>
}) {
  let content = blockContent;
  if (CONFIG.storyblok.version === 'draft') {
    content = handleEditorValues(EDITOR_VALUES.TESTIMONIAL, blockContent);
  }
  if (!content) {
    return (
      <Component {...others}>
        Referenced Testimonial is incorrect or deleted
      </Component>
    );
  }
  const {
    image,
    client,
    client_subtext: subtext,
    quote,
    link,
    link_text: linkText
  } = content;
  const cardProps: CardPropsType = {
    ...others
  };
  if (isValidLink(link)) {
    cardProps.link = link;
  }
  return (
    <Component {...cardProps}>
      <QuoteText>{quote}</QuoteText>
      <QuoteAuthor>
        <PersonImage src={image} alt={client} size={imageSize} />
        <QuoteAuthorText>
          <ContentText>{client}</ContentText>
          {subtext && <ClientSubtext>{subtext}</ClientSubtext>}
        </QuoteAuthorText>
        {link && linkText && (
          <CardHoverLink
            appearance="subtle"
            iconAfter={<Icon icon={faArrowRight} />}
          >
            {linkText}
          </CardHoverLink>
        )}
      </QuoteAuthor>
    </Component>
  );
}
